import React, { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { getStats, getLeads } from '../../lib/api';
import { Package, Tags, Building2, Users, FileText, ArrowRight, TrendingUp } from 'lucide-react';

export default function AdminDashboard() {
  const { refreshStats } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, leadsRes] = await Promise.all([
          getStats(),
          getLeads(),
        ]);
        setStats(statsRes.data);
        setRecentLeads(leadsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    refreshStats();
  }, [refreshStats]);

  const statCards = [
    { name: 'Categories', value: stats?.categories || 0, icon: Tags, path: '/admin/categories', color: 'bg-blue-500' },
    { name: 'Brands', value: stats?.brands || 0, icon: Building2, path: '/admin/brands', color: 'bg-purple-500' },
    { name: 'Products', value: stats?.products || 0, icon: Package, path: '/admin/products', color: 'bg-green-500' },
    { name: 'Leads', value: stats?.leads || 0, icon: Users, path: '/admin/leads', color: 'bg-amber-500' },
    { name: 'Blogs', value: stats?.blogs || 0, icon: FileText, path: '/admin/blogs', color: 'bg-pink-500' },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton h-28" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-serif text-[#0B1F3B]">Dashboard</h1>
        <p className="text-gray-500">Welcome to Passion of Aroma Admin Panel</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.path}
            className="bg-white p-6 border border-gray-100 hover:border-[#D4AF37]/30 transition-colors"
            data-testid={`stat-${stat.name.toLowerCase()}`}
          >
            <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-4`}>
              <stat.icon className="h-5 w-5 text-white" />
            </div>
            <div className="text-2xl font-bold text-[#0B1F3B]">{stat.value}</div>
            <div className="text-sm text-gray-500">{stat.name}</div>
          </Link>
        ))}
      </div>

      {/* Recent Leads */}
      <div className="bg-white border border-gray-100">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-lg font-serif text-[#0B1F3B]">Recent Leads</h2>
          <Link
            to="/admin/leads"
            className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1"
          >
            View All <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          {recentLeads.length > 0 ? (
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left">Name</th>
                  <th className="px-6 py-3 text-left">Company</th>
                  <th className="px-6 py-3 text-left">Email</th>
                  <th className="px-6 py-3 text-left">Source</th>
                  <th className="px-6 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.map((lead) => (
                  <tr key={lead.id}>
                    <td className="px-6 py-4 font-medium">{lead.name}</td>
                    <td className="px-6 py-4 text-gray-600">{lead.company || '-'}</td>
                    <td className="px-6 py-4 text-gray-600">{lead.email}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs uppercase tracking-wider text-gray-500">
                        {lead.source_page}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded ${
                        lead.status === 'new' ? 'bg-green-100 text-green-700' :
                        lead.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {lead.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center text-gray-500">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No leads yet. They'll appear here once visitors submit inquiries.</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Link
          to="/admin/products"
          className="bg-white p-6 border border-gray-100 hover:border-[#D4AF37]/30 transition-colors group"
        >
          <Package className="h-8 w-8 text-[#D4AF37] mb-4" />
          <h3 className="font-medium text-[#0B1F3B] group-hover:text-[#D4AF37] transition-colors">
            Add New Product
          </h3>
          <p className="text-sm text-gray-500 mt-1">Add products to your catalog</p>
        </Link>
        <Link
          to="/admin/brands"
          className="bg-white p-6 border border-gray-100 hover:border-[#D4AF37]/30 transition-colors group"
        >
          <Building2 className="h-8 w-8 text-[#D4AF37] mb-4" />
          <h3 className="font-medium text-[#0B1F3B] group-hover:text-[#D4AF37] transition-colors">
            Manage Brands
          </h3>
          <p className="text-sm text-gray-500 mt-1">Add or edit brand information</p>
        </Link>
        <Link
          to="/admin/leads"
          className="bg-white p-6 border border-gray-100 hover:border-[#D4AF37]/30 transition-colors group"
        >
          <Users className="h-8 w-8 text-[#D4AF37] mb-4" />
          <h3 className="font-medium text-[#0B1F3B] group-hover:text-[#D4AF37] transition-colors">
            View All Leads
          </h3>
          <p className="text-sm text-gray-500 mt-1">Manage customer inquiries</p>
        </Link>
      </div>
    </div>
  );
}
